
import { bULKOU } from "../_utils/bulkOrderUtils";
import { useEffect, useMemo, useRef } from "react";
import { getProxyBulkOrderColumns } from "../_utils/ProxyBulkOrderColumns";
import type { SpreadsheetTableHandle } from "../Proxy/pages/GiftCardForm/_sections/ProxyBulkOrderTable";
import type { SpreadsheetRecord } from "@gistapps/composite-spreadsheet";

declare global {
  interface Window {
    GCPSDK:any;
  }
}

export default function useBulkOrder({
  settings,
  loading,
  bulkOrder,
  amountChoices,
  conversionSurcharge,
  pricingConfig,
  currency,
  updateFormField,
}: any) {

  bULKOU.settings = settings;
  bULKOU.amountChoices = amountChoices;
  bULKOU.conversionSurcharge = conversionSurcharge;
  bULKOU.pricingConfig = pricingConfig;
  bULKOU.currency = currency;

  const bulkSpreadsheetRef = useRef<SpreadsheetTableHandle>(null);

  const { columns: spreadsheetColumns, columnTypeConfig } = useMemo(() => {

    if (!settings || !amountChoices?.values?.length) {
      return { columns: [], columnTypeConfig: {} };
    }

    return getProxyBulkOrderColumns(settings, amountChoices);

  }, [settings, amountChoices]);

  /**
   * Handle cell activation: update the form fields and navigate to the correct step.
   */
  const handleCellActivated = (
    _rowIndex: number,
    record: SpreadsheetRecord,
    columnKey?: string,
  ): void => {

    const fieldName = columnKey ? columnKey.replaceAll("__", ".") : "";

    Object.entries(record).forEach(([key, value]) => {
      const field = key.replaceAll("__", ".");
      window.GCPSDK.form.set(field, value ?? "");
    });

    if (fieldName) {
      bULKOU.updateStep(fieldName);
    }

  };

  /**
   * Handle the submit button click: validate table, then add to cart or create draft order.
   */
  const handleSubmitClick = async () => {

    const records = await bulkSpreadsheetRef.current?.submitTable();

    if (!records) return;

    const mergedRequests = bULKOU.mergeRequests(records);

    const cartLimit = 100;

    let cartItemCount = 0;
    try {
      const cartResponse = await fetch(`${window.Shopify?.routes?.root || ""}cart.js`).then((r) => r.json());
      cartItemCount = cartResponse.item_count || 0;
    } catch {
      // proceed without cart count
    }

    const useDraftOrder = cartItemCount + records.length > cartLimit;
    const chunkSize = 10;

    bULKOU.addToCartProgressStart();

    if (useDraftOrder) {
      bULKOU.beginFakeProgress();
      bULKOU.processGenerateDraftOrder(mergedRequests);
    } else {
      const { chunkArray } = await import("@root/_utils");
      const requestGroups = chunkArray(mergedRequests, chunkSize);
      bULKOU.totalRequests = requestGroups.length;
      bULKOU.processAddToCart(requestGroups);
    }

  };

  /**
   * Wire up DOM action buttons once the spreadsheet and settings are ready.
   */
  useEffect(() => {

    if (loading || !settings || !bulkOrder || !amountChoices?.values?.length) return;

    window.GCPSDK = window.GCPSDK || {};
    window.GCPSDK.form = window.GCPSDK.form || {};
    window.GCPSDK.form.set = (field: string, data: any) => {
      updateFormField(field, data, true);
    };

    const submitBtn = document.getElementById("gcpGenerateOrders");
    const exportBtn = document.getElementById("gcpExportTable");

    const onSubmit = () => { handleSubmitClick(); };
    const onExport = () => { bulkSpreadsheetRef.current?.exportTable(); };

    submitBtn?.addEventListener("click", onSubmit);
    exportBtn?.addEventListener("click", onExport);

    return () => {
      submitBtn?.removeEventListener("click", onSubmit);
      exportBtn?.removeEventListener("click", onExport);
    };

  }, [loading, settings, bulkOrder, amountChoices]);

  return {
    bulkSpreadsheetRef,
    spreadsheetColumns,
    columnTypeConfig,
    handleCellActivated,
  };

}
